import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronUp, ChevronDown, Loader2, Power, PowerOff, Headphones } from "lucide-react";
import api from "@/lib/api";

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "textarea", label: "Texto longo" },
  { value: "number", label: "Número" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Telefone" },
  { value: "date", label: "Data" },
  { value: "time", label: "Hora" },
  { value: "select", label: "Lista suspensa" },
  { value: "radio", label: "Escolha única (pills)" },
  { value: "yesno", label: "Sim / Não" },
  { value: "checkboxes", label: "Múltipla escolha (chips)" },
];
const HAS_OPTIONS = new Set(["select", "radio", "checkboxes"]);

const genId = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now() + Math.random());

const inp = "w-full h-10 bg-card border border-border rounded-lg px-3 text-[14px] text-foreground focus:outline-none focus:border-primary";
const ta = "w-full bg-card border border-border rounded-lg px-3 py-2 text-[14px] text-foreground focus:outline-none focus:border-primary";
const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-1">{children}</label>
);

function FieldEditor({ field, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...field, [k]: v });
  const showOpts = HAS_OPTIONS.has(field.type);
  return (
    <div className="border border-border rounded-lg p-4 bg-muted/40 space-y-3" data-testid={`field-editor-${field.key}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Campo</div>
        <button onClick={onRemove} className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" data-testid={`remove-field-${field.key}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Chave interna *</Label>
          <input className={inp} value={field.key || ""} onChange={(e)=>set("key", e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase())} placeholder="ex: origem_referencia" />
        </div>
        <div>
          <Label>Rótulo visível *</Label>
          <input className={inp} value={field.label || ""} onChange={(e)=>set("label", e.target.value)} placeholder="Ex: Como nos conheceu?" />
        </div>
        <div>
          <Label>Tipo</Label>
          <select className={inp} value={field.type || "text"} onChange={(e)=>set("type", e.target.value)}>
            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label>Placeholder</Label>
          <input className={inp} value={field.placeholder || ""} onChange={(e)=>set("placeholder", e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label>Ajuda (helper)</Label>
          <input className={inp} value={field.helper || ""} onChange={(e)=>set("helper", e.target.value)} placeholder="Texto de apoio pequeno debaixo do campo" />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-foreground col-span-2">
          <input type="checkbox" checked={!!field.required} onChange={(e)=>set("required", e.target.checked)} className="accent-[#1D4ED8]" />
          Obrigatório
        </label>
      </div>
      {showOpts && (
        <div>
          <Label>Opções (uma por linha — formato: valor|rótulo)</Label>
          <textarea
            rows={4}
            className={ta}
            value={(field.options || []).map(o => `${o.value}|${o.label}`).join("\n")}
            onChange={(e) => {
              const opts = e.target.value.split("\n").map(line => {
                const [v, ...rest] = line.split("|");
                if (!v) return null;
                return { value: v.trim(), label: (rest.join("|") || v).trim() };
              }).filter(Boolean);
              set("options", opts);
            }}
            placeholder={"sim|Sim\nnao|Não"}
          />
        </div>
      )}
    </div>
  );
}

function StepEditor({ step, index, total, onChange, onRemove, onMove }) {
  const set = (k, v) => onChange({ ...step, [k]: v });
  const addField = () => set("fields", [...(step.fields || []), { id: genId(), key: `campo_${(step.fields||[]).length + 1}`, label: "Novo campo", type: "text", options: [], required: false }]);
  const updateField = (idx, next) => set("fields", (step.fields || []).map((f, i) => i === idx ? next : f));
  const removeField = (idx) => set("fields", (step.fields || []).filter((_, i) => i !== idx));
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid={`step-editor-${step.key}`}>
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="disabled:opacity-30 text-muted-foreground hover:text-foreground"><ChevronUp className="w-4 h-4" /></button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="disabled:opacity-30 text-muted-foreground hover:text-foreground"><ChevronDown className="w-4 h-4" /></button>
        </div>
        <div className="text-[10px] uppercase tracking-[0.16em] text-foreground font-semibold flex-1">Passo custom {index + 1}</div>
        <button onClick={onRemove} className="text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))] text-xs inline-flex items-center gap-1" data-testid={`remove-step-${step.key}`}>
          <Trash2 className="w-3.5 h-3.5" /> Remover passo
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Chave interna *</Label>
          <input className={inp} value={step.key || ""} onChange={(e)=>set("key", e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase())} />
        </div>
        <div>
          <Label>Rótulo curto</Label>
          <input className={inp} value={step.label || ""} onChange={(e)=>set("label", e.target.value)} />
        </div>
        <div className="col-span-2">
          <Label>Título (kicker)</Label>
          <input className={inp} value={step.kicker || ""} onChange={(e)=>set("kicker", e.target.value)} placeholder="Ex: Origem do contacto" />
        </div>
        <div className="col-span-2">
          <Label>Pergunta ao cliente (aceita HTML simples: &lt;b&gt;)</Label>
          <textarea rows={3} className={ta} value={step.question || ""} onChange={(e)=>set("question", e.target.value)} placeholder='"Onde ouviu falar de nós? Foi um amigo, uma pesquisa Google?"' />
        </div>
        <div className="col-span-2">
          <Label>Dica (visível ao atendedor)</Label>
          <input className={inp} value={step.hint || ""} onChange={(e)=>set("hint", e.target.value)} placeholder="Guia para o atendimento não visível ao cliente" />
        </div>
      </div>
      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Campos ({(step.fields || []).length})</div>
          <button onClick={addField} className="text-[11px] uppercase tracking-[0.14em] font-semibold px-3 py-1.5 rounded-full bg-foreground hover:bg-foreground/90 text-background inline-flex items-center gap-1.5" data-testid={`add-field-${step.key}`}>
            <Plus className="w-3 h-3" /> Adicionar campo
          </button>
        </div>
        <div className="space-y-3">
          {(step.fields || []).map((f, i) => (
            <FieldEditor key={f.id || i} field={f} onChange={(next) => updateField(i, next)} onRemove={() => removeField(i)} />
          ))}
          {(step.fields || []).length === 0 && (
            <div className="text-[12px] text-muted-foreground/70">Sem campos — clique em "Adicionar campo".</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ScriptSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/script/config").then((r) => setCfg(r.data));
  }, []);

  if (!cfg) return <div className="p-8 text-muted-foreground">A carregar…</div>;

  const setActive = async () => {
    try {
      const { data } = await api.post("/script/config/toggle-active");
      setCfg((c) => ({ ...c, active: data.active }));
      toast.success(data.active ? "Guião ATIVADO" : "Guião DESATIVADO");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alternar");
    }
  };

  const addStep = () => setCfg((c) => ({
    ...c, custom_steps: [...(c.custom_steps || []), {
      id: genId(), key: `custom_${(c.custom_steps || []).length + 1}`,
      kicker: "Novo passo", label: "Extra", question: "Pergunta ao cliente...",
      hint: "", icon: "sparkles", mandatory: false, fields: [],
    }],
  }));

  const updateStep = (idx, next) => setCfg((c) => ({
    ...c, custom_steps: (c.custom_steps || []).map((s, i) => i === idx ? next : s),
  }));

  const removeStep = (idx) => setCfg((c) => ({
    ...c, custom_steps: (c.custom_steps || []).filter((_, i) => i !== idx),
  }));

  const moveStep = (idx, dir) => setCfg((c) => {
    const arr = [...(c.custom_steps || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return c;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...c, custom_steps: arr };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/script/config", cfg);
      toast.success("Configuração guardada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 lg:p-12 max-w-5xl mx-auto" data-testid="script-settings-root">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configurações · Guião de Atendimento</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight flex items-center gap-3">
            <Headphones className="w-8 h-8 text-foreground" /> Guião de Atendimento
          </h1>
          <p className="text-muted-foreground text-sm mt-2">Gerir a ativação e adicionar passos personalizados ao final das 7 etapas base.</p>
        </div>
        <button
          onClick={setActive}
          data-testid="toggle-active"
          className={`inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full border ${
            cfg.active
              ? "bg-[hsl(var(--success-bg))] border-[#059669]/30 text-[#059669]"
              : "bg-[hsl(var(--warning-bg))] border-[#DC2626]/30 text-[hsl(var(--danger))]"
          }`}>
          {cfg.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
          {cfg.active ? "ATIVO — clique para desativar" : "INATIVO — clique para ativar"}
        </button>
      </div>

      {/* Built-in steps summary */}
      <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="builtin-summary">
        <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-3">Passos base (fixos)</div>
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[13px] text-[#374151] list-decimal list-inside">
          <li>Identificação do contacto</li>
          <li>Localização <span className="text-[10px] uppercase font-semibold text-[hsl(var(--danger))]">Obrigatória</span></li>
          <li>Transporte público regular <span className="text-[10px] uppercase font-semibold text-[hsl(var(--danger))]">Obrigatório</span></li>
          <li>Pessoa cuidada (com toggle 2º beneficiário)</li>
          <li>Contexto do pedido (serviço · urgência · necessidades)</li>
          <li>Notas do atendimento</li>
          <li>Agendar Reunião Técnica</li>
        </ol>
        <p className="text-[11px] text-muted-foreground mt-3">Os passos base não podem ser removidos — asseguram os campos obrigatórios para a estimativa.</p>
      </div>

      {/* Custom steps */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-foreground">Passos personalizados</h2>
        <button onClick={addStep} data-testid="add-step" className="text-xs uppercase tracking-[0.14em] font-semibold px-4 py-2 rounded-full bg-foreground hover:bg-slate-800 text-background inline-flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Adicionar passo
        </button>
      </div>

      <div className="space-y-4" data-testid="steps-list">
        {(cfg.custom_steps || []).map((s, i) => (
          <StepEditor
            key={s.id || i}
            step={s}
            index={i}
            total={(cfg.custom_steps || []).length}
            onChange={(next) => updateStep(i, next)}
            onRemove={() => removeStep(i)}
            onMove={(dir) => moveStep(i, dir)}
          />
        ))}
        {(cfg.custom_steps || []).length === 0 && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Sem passos personalizados. Clique em "Adicionar passo" para criar.
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-end sticky bottom-0 bg-muted/40 py-4 border-t border-border">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-config"
          className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 disabled:opacity-60 text-background font-semibold rounded-full px-6 py-3 text-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar configuração
        </button>
      </div>
    </div>
  );
}
